import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { UIRouterModule } from '@uirouter/angular';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DatePipe, Location } from '@angular/common';
import { TagInputModule } from 'ngx-chips';
import { SweetAlert2Module } from '@toverux/ngx-sweetalert2';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { HttpCustomService } from './services/http-custom.service';
import { UserManagementService } from './services/user-management.service';
import { ProjectManagementService } from './services/project-management.service';
import { DataService } from './services/data.service';
import { LogService } from './services/log.service';
import { AppComponent } from './app.component';
import { CreateProjectComponent } from './components/create-project/create-project.component';
import { HeaderComponent } from './components/header/header.component';
import { LoginComponent } from './components/login/login.component';
import { SignupComponent } from './components/signup/signup.component';
import { UserManagementComponent } from './components/user-management/user-management.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { CreateUserComponent } from './components/create-user/create-user.component';
import { EditUserComponent } from './components/edit-user/edit-user.component';
import { UploadFilesComponent } from './components/upload-files/upload-files.component';
import { NgbCarouselModule, NgbAlertModule, NgbTabsetModule, NgbDropdownModule, NgbModalModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { HomeComponent } from './components/home/home.component';
import { Select2Module } from "ng2-select2";
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { TimelineComponent } from './components/timeline/timeline.component';
import { ProjectWallComponent } from './components/project-wall/project-wall.component';
import { FileDropModule } from 'ngx-file-drop';
import { MultipleDatePickerModule } from './components/multiple-date-picker-angular';
import { EditProjectComponent } from './components/edit-project/edit-project.component';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { HelpComponent } from './components/help/help.component';
import { XmppService } from "./services/xmpp.service";
import { FrontEndService } from "./services/front-end.service";
import { TinyEditorComponent } from './components/tiny-editor/tiny-editor.component';
import { NgxLoadingModule} from "ngx-loading";
import { SafehtmlPipe } from './pipes/safehtml.pipe';
import { ToastyModule } from 'ng2-toasty';
import { FocusDirective } from './directives/focus.directive';
import { ChatService} from "./services/chat.service";
import { AppModalComponent} from "./components/app-modal/app-modal.component";
import { EmailNotificationsComponent } from './components/email-notifications/email-notifications.component';
import { EvaluationComponent } from './components/evaluation/evaluation.component';
import { ViewFeedbackComponent } from './components/view-feedback/view-feedback.component';
import { ProjectAttachmentsTabComponent } from './components/project-attachments-tab/project-attachments-tab.component';
import { ProjectEvaluationTabComponent } from './components/project-evaluation-tab/project-evaluation-tab.component';
import { ProjectDetailsTabComponent } from './components/project-details-tab/project-details-tab.component';
import { ProjectUsersTabComponent } from './components/project-users-tab/project-users-tab.component';
import { ProjectChatsTabComponent } from './components/project-chats-tab/project-chats-tab.component';
import { ProjectHomeComponent } from './components/project-home/project-home.component';
import { WallReplyComponent } from './components/wall-reply/wall-reply.component';
import { CreateWallPostComponent } from './components/create-wall-post/create-wall-post.component';
import { SinglePostComponent } from './components/single-post/single-post.component';
import { BackupAndRestoreComponent } from './components/backup-and-restore/backup-and-restore.component';
import { BackupService } from './services/backup.service';
import { FindRolePipe } from './pipes/find-role.pipe';
import { ViewUserProfileListComponent } from './components/view-user-profile-list/view-user-profile-list.component';
import { ViewUserProfileDetailsComponent } from './components/view-user-profile-details/view-user-profile-details.component';
import { CreateUserProfileDetailsComponent } from './components/create-user-profile-details/create-user-profile-details.component';
import { UserProfileService } from "./services/user-profile.service";
import { ImageUploadModule } from "angular2-image-upload";
import { ProjectTasksComponent } from './components/project-tasks/project-tasks.component';
import { WorkSpaceService } from "./services/work-space.service";
import { ClickOutsideModule } from "ng-click-outside";
import { WorkspaceDashboardComponent } from './components/workspace-dashboard/workspace-dashboard.component';
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { DashboardService } from "./services/dashboard.service";
import { NgxChartsModule } from "@swimlane/ngx-charts";
import { NgMultiSelectDropDownModule } from "ng-multiselect-dropdown";
import { ReadMoreComponent } from './components/read-more/read-more.component';
import { ProjectWallTabComponent } from './components/project-wall-tab/project-wall-tab.component';
import { InViewportModule } from "ng-in-viewport";
import { ModifyColumnsComponent } from './components/modify-columns/modify-columns.component';
import { SliderWithLabelComponent } from './components/slider-with-label/slider-with-label.component';

/********************** General States **********************/
const loginState = { name: 'login', url: '/login',  component: LoginComponent };
const signupState = { name: 'signup', url: '/signup',  component: SignupComponent };
const notFound = { name: 'notFound', url: '/404',  component: NotFoundComponent };
const forbidden = { name: 'Forbidden', url: '/forbidden',  component: ForbiddenComponent  };
const forgotPassword = { name: 'forgotPassword', url: '/forgot-password',  component: ForgotPasswordComponent };
const feedback = { name: 'Feedback', url: '/feedback',  component: FeedbackComponent };
const backupState = { name: 'backup', url: '/backup',  component: BackupAndRestoreComponent };
const help = { name: 'Help', url: '/help',  component: HelpComponent };
const UserManagementState = { name: 'userManagement', url: '/user-management/:user_id',  component: UserManagementComponent };
const UserProfileState = { name: 'userProfile', url: '/user-profile',  component: UserProfileComponent };
const EditUserProfileState = { name: 'EditUserProfile', url: '/edit-user-profile/:user_id',  component: EditUserComponent };
const CreateUserProfileState = { name: 'CreateUserProfile', url: '/create-user-profile',  component: CreateUserComponent };
const ResetPasswordState = { name: 'resetPassword', url: '/reset-password/:user_id',  component: ResetPasswordComponent };
const emailNotificationState = { name: 'emailNotification', url: '/email-notification-settings',  component: EmailNotificationsComponent };
const readFeedbackState = { name: 'readFeedback', url: '/read-feedback',  component: ViewFeedbackComponent };
const homeState = { name: 'home', url: '/',  component: HomeComponent };
/******************** General States Ends ********************/

/********************** Project States **********************/
const projectHomeState = { name: 'projectHome', url: '/projects',  component: ProjectHomeComponent };
const projectState = { name: 'project', url: '/project/:project_id',  component: HomeComponent };
const projectUsersState = { name: 'projectUsers', url: '/project/:project_id/users',  component: HomeComponent };
const projectAttachmentsState = { name: 'projectAttachments', url: '/project/:project_id/attachments',  component: HomeComponent };
const projectDetailsState = { name: 'projectDetails', url: '/project/:project_id/details',  component: HomeComponent };
const projectEvaluationState = { name: 'projectEvaluationTab', url: '/project/:project_id/evaluation',  component: HomeComponent };
const singlePostState = { name: 'singlePost', url: '/project/:project_id/wall/:post_id',  component: SinglePostComponent };
const evaluationState = { name: 'projectEvaluation', url: '/evaluation/:project_id',  component: EvaluationComponent };
const createProjectState = { name: 'createProject', url: '/create-project',  component: CreateProjectComponent };
const editProjectState = { name: 'editProject', url: '/edit-project/:project_id',  component: EditProjectComponent };
/******************** Project States Ends ********************/

/************************* Chat States ***********************/
const projectChatsState = { name: 'projectChats', url: '/project/:project_id/chats/:user_id',  component: HomeComponent };
/********************** Chat States Ends *********************/

/********************** Profile States ***********************/
const profileList = { name: 'profileList', url: '/profiles',  component: ViewUserProfileListComponent };
const profileDetails = { name: 'profileDetails', url: '/profiles/details/:user_id',  component: ViewUserProfileDetailsComponent };
const createProfile = { name: 'createProfile', url: '/profiles/create',  component: CreateUserProfileDetailsComponent };
const editProfile = { name: 'editProfile', url: '/profiles/edit/:user_id',  component: CreateUserProfileDetailsComponent };
/******************** Profile States Ends *********************/

/********************** Workspace States **********************/
const taskState = { name: 'tasks', url: '/tasks',  component: ProjectTasksComponent };
const dashboardState = { name: 'dashboard', url: '/dashboard',  component: WorkspaceDashboardComponent };
const modifyColumnState = { name: 'modifyColumns', url: '/modify-columns',  component: ModifyColumnsComponent };
/******************** Workspace States Ends *******************/

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, '/assets/translations/', '.json');
}

@NgModule({
    declarations: [
        AppComponent,
        CreateProjectComponent,
        HeaderComponent,
        LoginComponent,
        SignupComponent,
        UserManagementComponent,
        UserProfileComponent,
        ResetPasswordComponent,
        CreateUserComponent,
        EditUserComponent,
        HomeComponent,
        ForbiddenComponent,
        ForgotPasswordComponent,
        NotFoundComponent,
        TimelineComponent,
        ProjectWallComponent,
        EditProjectComponent,
        FeedbackComponent,
        HelpComponent,
        TinyEditorComponent,
        SafehtmlPipe,
        FocusDirective,
        AppModalComponent,
        SafehtmlPipe,
        EmailNotificationsComponent,
        EvaluationComponent,
        ViewFeedbackComponent,
        ProjectAttachmentsTabComponent,
        ProjectEvaluationTabComponent,
        ProjectDetailsTabComponent,
        ProjectUsersTabComponent,
        ProjectChatsTabComponent,
        ProjectHomeComponent,
        WallReplyComponent,
        CreateWallPostComponent,
        SinglePostComponent,
        BackupAndRestoreComponent,
        FindRolePipe,
        ViewUserProfileListComponent,
        ViewUserProfileDetailsComponent,
        CreateUserProfileDetailsComponent,
        UploadFilesComponent,
        ProjectTasksComponent,
        WorkspaceDashboardComponent,
        ReadMoreComponent,
        ProjectWallTabComponent,
        ModifyColumnsComponent,
        SliderWithLabelComponent
    ],
    entryComponents: [AppModalComponent, CreateWallPostComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        HttpClientModule,
        FileDropModule,
        InfiniteScrollModule,
        InViewportModule,
        TagInputModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        NgbModule.forRoot(),
        NgbModalModule.forRoot(),
        NgbTabsetModule.forRoot(),
        NgbCarouselModule.forRoot(),
        NgbAlertModule.forRoot(),
        NgbDropdownModule.forRoot(),
        MultipleDatePickerModule,
        NgxLoadingModule.forRoot({}),
        ToastyModule.forRoot(),
        Select2Module,
        ImageUploadModule.forRoot(),
        NgxChartsModule,
        NgxDatatableModule,
        ClickOutsideModule,
        NgMultiSelectDropDownModule.forRoot(),
        UIRouterModule.forRoot({
            states: [
                loginState,
                signupState,
                createProjectState,
                editProjectState,
                UserManagementState,
                UserProfileState,
                EditUserProfileState,
                CreateUserProfileState,
                ResetPasswordState,
                homeState,
                projectState,
                projectUsersState,
                projectAttachmentsState,
                projectDetailsState,
                projectChatsState,
                projectEvaluationState,
                projectHomeState,
                notFound,
                forbidden,
                forgotPassword,
                feedback,
                emailNotificationState,
                help,
                evaluationState,
                readFeedbackState,
                singlePostState,
                backupState,
                profileList,
                profileDetails,
                editProfile,
                createProfile,
                taskState,
                dashboardState,
                modifyColumnState
            ], otherwise: '/404', useHash: false }),
            SweetAlert2Module.forRoot({
                buttonsStyling: false,
                customClass: 'modal-content swalbtn',
                confirmButtonClass: 'btn btn-success',
                cancelButtonClass: 'btn btn-outline-success'
            })
    ],
    providers: [HttpCustomService, UserManagementService, DataService, ProjectManagementService, Location, XmppService, FrontEndService, ChatService, LogService, BackupService, UserProfileService, WorkSpaceService, DashboardService, DatePipe],
    bootstrap: [AppComponent]
})
export class AppModule {
}

